import { authorizedFetch } from '../auth/authorizedFetch';

/**
 * The producer's API. Everything here is read-only and money-free by
 * construction on the server (see `deal/producer_views.py`); the one write is
 * filing a sale invoice. There is deliberately no rand figure in any type.
 */

export type Stage = 'Being set up' | 'In progress' | 'Slaughtered' | 'Complete';

export interface Counts {
  total: number;
  alive: number;
  slaughtered: number;
  deceased: number;
  sold: number;
}

export interface Performance {
  avg_daily_gain_kg: number | null;
  avg_days_in_feedlot: number | null;
  feed_conversion: number | null;
  feed_conversion_is_estimate?: boolean;
  dressing_percentage: number | null;
  weight_gain_kg?: number | null;
  sample_size?: number;
  deals_measured?: number;
}

export interface IntegrationCard {
  key: 'rmis' | 'precision_farm' | 'sa_studbook' | string;
  name: string;
  blurb: string;
  state: 'active' | 'ready' | 'soon';
  detail: string;
}

export interface Overview {
  producers: string[];
  deals: { total: number; open: number; complete: number };
  animals: {
    through_avcs: number;
    alive: number;
    slaughtered: number;
    deceased: number;
    movements_recorded: number;
  };
  performance: Performance;
  integrations: IntegrationCard[];
}

export interface DealCard {
  id: string;
  deal_no: string;
  reference: string;
  species: string | null;
  programme: string | null;
  started: string | null;
  stage: Stage;
  counts: Counts;
}

export interface MovementStep {
  id: string;
  code: string;
  label: string;
  kind: 'farm' | 'movement' | 'feedlot' | 'abattoir';
  date: string | null;
  place: string | null;
  animals: number;
}

export interface HealthRecord {
  id: string;
  kind: 'vaccination' | 'treatment';
  name: string;
  type: string;
  date: string | null;
  animals: number | null;
  withdrawal_ends: string | null;
}

export interface ProducerDocument {
  id: string;
  name: string;
  reference: string;
  date: string | null;
  file: string | null;
  mine: boolean;
}

export interface DealDetail extends DealCard {
  performance: Performance;
  movement: MovementStep[];
  health: HealthRecord[];
  documents: ProducerDocument[];
  can_upload_invoice: boolean;
}

export interface AnimalRow {
  id: string;
  vid: string;
  eid: string;
  sex: string;
  breed: string;
  status: string;
  status_code: 'ACTIVE' | 'SLAUGHTERED' | 'DECEASED' | 'SOLD';
  location: string;
  last_weight_kg: number | null;
  last_weighed: string | null;
}

export class ProducerApiError extends Error {
  fields: Record<string, string>;
  status: number;
  constructor(message: string, status: number, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

async function json<T>(res: Response): Promise<T> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* an empty or non-JSON body is handled below */
  }
  if (!res.ok) {
    const fields: Record<string, string> = {};
    if (body && typeof body === 'object') {
      for (const [k, v] of Object.entries(body)) {
        fields[k] = Array.isArray(v) ? String(v[0]) : String(v);
      }
    }
    const message =
      (res.status === 404 ? 'We could not find that deal. It may belong to someone else, or it may have been removed.'
        : fields.detail) ||
      (res.status === 403
        ? 'You do not have access to that.'
        : 'Something went wrong. Please try again.');
    throw new ProducerApiError(message, res.status, fields);
  }
  return body as T;
}

const get = async <T,>(path: string): Promise<T> => json<T>(await authorizedFetch(path));

export const fetchOverview = () => get<Overview>('/api/producer/overview/');
export const fetchDealCards = async () =>
  (await get<{ results: DealCard[] }>('/api/producer/deals/')).results;
export const fetchDealDetail = (id: string) =>
  get<DealDetail>(`/api/producer/deals/${encodeURIComponent(id)}/`);

export async function fetchAnimals(
  id: string,
  opts: { q?: string; status?: string; offset?: number; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.status) qs.set('status', opts.status);
  qs.set('limit', String(opts.limit ?? 50));
  qs.set('offset', String(opts.offset ?? 0));
  return get<{ count: number; results: AnimalRow[] }>(
    `/api/producer/deals/${encodeURIComponent(id)}/animals/?${qs.toString()}`,
  );
}

export async function uploadInvoice(
  id: string,
  input: { file: File; reference_number: string; date_of_document: string; amount?: string },
): Promise<ProducerDocument> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('reference_number', input.reference_number);
  form.append('date_of_document', input.date_of_document);
  if (input.amount) form.append('amount', input.amount);
  const res = await authorizedFetch(`/api/producer/deals/${encodeURIComponent(id)}/invoices/`, {
    method: 'POST',
    body: form,
  });
  return json<ProducerDocument>(res);
}
